import moment from 'moment';

import { getValueForToggle } from '../service/InternalGroupHelpers/InternalGroupHelpers';
import {
    addLeadingDash,
    getDefaultTagCloudTargetingState,
    getTagCloudTargetingId,
    isFiltering,
    isTargeting,
    mapTagValueToString
} from '../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { DictionaryItem } from '../typings/common';
import { DatesIntervalEditorsState } from '../typings/datesInterval';
import {
    GdAdditionalTargetingJoinType,
    GdAdditionalTargetingMode,
    GdAdditionalTargetingUatraitsInput
} from '../typings/graphql-grid';
import { EditInternalGroupData } from '../typings/groupsEditState';
import {
    AdditionalTargetingKey,
    BinaryControlExternalValue,
    BinaryControlOwnValue,
    InputExternalValue,
    InternalGroupTargetingValueItem,
    StringDictExternalValue,
    TargetingShowDates,
    TernaryControlExternalValue,
    TernaryControlOwnValue
} from '../typings/internalGroups';
import {
    TagCloudTargeting,
    TagCloudTargetingsState,
    TargetingValueItem
} from '../typings/tagCloudTargeting';
import { Omit } from '../typings/utils';

export const mapTagCloudTargetingValue = (
    editor: TagCloudTargeting
): TargetingShowDates | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { includedValue, excludedValue } = editor;

    if (includedValue.length === 0 && excludedValue.length === 0) {
        return undefined;
    }

    if (includedValue.length === 0 && excludedValue.length !== 0) {
        return {
            targetingMode: FILTERING,
            joinType: ALL,
            value: [...excludedValue.map(mapTagValueToString)]
        };
    }

    return {
        targetingMode: TARGETING,
        joinType: ANY,
        value: [
            ...includedValue.map(mapTagValueToString),
            ...excludedValue.map(mapTagValueToString).map(addLeadingDash)
        ]
    };
};

export const mapTernaryControlExternalValue = (
    value?: TernaryControlExternalValue
): TernaryControlOwnValue => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { YES, NO, NOT_SELECTED } = TernaryControlOwnValue;

    if (value) {
        const { joinType, targetingMode } = value;

        if (joinType === ANY && targetingMode === TARGETING) {
            return YES;
        }

        if (joinType === ALL && targetingMode === FILTERING) {
            return NO;
        }
    }

    return NOT_SELECTED;
};

export const mapTernaryControlOwnValue = (
    value?: TernaryControlOwnValue
): TernaryControlExternalValue => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { YES, NO } = TernaryControlOwnValue;

    switch (value) {
        case YES:
            return {
                joinType: ANY,
                targetingMode: TARGETING
            };
        case NO:
            return {
                joinType: ALL,
                targetingMode: FILTERING
            };
        default:
            return {
                joinType: GdAdditionalTargetingJoinType.ANY,
                targetingMode: GdAdditionalTargetingMode.TARGETING,
                uncheckAll: true
            };
    }
};

export const mapBinaryControlExternalValue = (
    value?: BinaryControlExternalValue
): BinaryControlOwnValue | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { INCLUDED, EXCLUDED } = BinaryControlOwnValue;

    if (value) {
        if (value.joinType === ANY && value.targetingMode === TARGETING) {
            return INCLUDED;
        }

        if (value.joinType === ALL && value.targetingMode === FILTERING) {
            return EXCLUDED;
        }
    }

    return undefined;
};

export const mapBinaryControlOwnValue = (
    value?: BinaryControlOwnValue
): Exclude<Omit<BinaryControlExternalValue, 'value'>, undefined> => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { INCLUDED, EXCLUDED } = BinaryControlOwnValue;

    switch (value) {
        case INCLUDED:
            return {
                joinType: ANY,
                targetingMode: TARGETING
            };
        case EXCLUDED:
            return {
                joinType: ALL,
                targetingMode: FILTERING
            };
        default:
            return {
                joinType: ANY,
                targetingMode: TARGETING
            };
    }
};

export const mapStringDictsExternalValue = (
    value?: StringDictExternalValue
): string[] => {
    if (!value || !value.value) {
        return [];
    }

    return value.value.map(it => it.targetingValueEntryId.toString());
};

export const mapStringDictsOwnValue = (
    value: string[]
): StringDictExternalValue['value'] => {
    return value.map(it => ({
        targetingValueEntryId: it
    }));
};

export const mapInputExternalValue = (value?: InputExternalValue): string => {
    if (!value || !value.value) {
        return '';
    }

    return value.value;
};

export const mapExternalValueForTargeting = (
    joinType: GdAdditionalTargetingJoinType,
    targetingMode: GdAdditionalTargetingMode,
    value: string | (string | GdAdditionalTargetingUatraitsInput)[],
    stringDict?: DictionaryItem[]
): InternalGroupTargetingValueItem[] => {
    if (!value) {
        return [];
    }

    const isPositive =
        joinType === GdAdditionalTargetingJoinType.ANY &&
        targetingMode === GdAdditionalTargetingMode.TARGETING;

    return (typeof value === 'string' ? value.split(',') : value)
        .map(
            (
                it: string | GdAdditionalTargetingUatraitsInput,
                index
            ): InternalGroupTargetingValueItem => {
                if (typeof it === 'string') {
                    return {
                        id: index.toString(),
                        name: isPositive ? it : `-${it}`
                    };
                }

                const textValue =
                    stringDict &&
                    stringDict.find(
                        item => item.val === it.targetingValueEntryId.toString()
                    );

                if (!textValue) {
                    return {
                        id: index.toString(),
                        name: ''
                    };
                }

                return {
                    id: textValue.val,
                    name: isPositive ? textValue.text : `-${textValue.text}`
                };
            }
        )
        .filter(it => Boolean(it.name));
};

export const mapInputOwnValue = (value: string): string => {
    // FIXME: @alex-sannikov костылище для старого дезигна. В новом будет выпилено во славу дна
    // https://st.yandex-team.ru/DIRECT-96347

    return value;
};

// Эта функция - пожелание маркетологов
//
// Кейс: Маркетолог выбирает тип таргетинга, значения, потом удаляет значения.
//       Тип таргетинга обнуляется. Затем он вводит или выбирает значения заново
//       и ожидает увидеть ранее выбранный тип таргетинга.
export const mapJoinType = (
    value?: BinaryControlExternalValue | TernaryControlExternalValue,
    serverValue?: BinaryControlExternalValue | TernaryControlExternalValue
): GdAdditionalTargetingJoinType => {
    if (!serverValue) {
        serverValue = value;
    }

    if (!value) {
        return serverValue
            ? serverValue.joinType
            : GdAdditionalTargetingJoinType.ANY;
    }

    return value.joinType;
};

// См. функцию mapJoinType
export const mapTargetingMode = (
    value?: BinaryControlExternalValue | TernaryControlExternalValue,
    serverValue?: BinaryControlExternalValue | TernaryControlExternalValue
): GdAdditionalTargetingMode => {
    if (!serverValue) {
        serverValue = value;
    }

    if (!value) {
        return serverValue
            ? serverValue.targetingMode
            : GdAdditionalTargetingMode.TARGETING;
    }

    return value.targetingMode;
};

export const mapDatesIntervalEditorsDataFromInternalGroups = (
    groupRecord: Record<string, EditInternalGroupData>
): DatesIntervalEditorsState | undefined => {
    const groupIds = Object.keys(groupRecord);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId) => {
        const currentGroupValue = groupRecord[groupId].value;
        const {
            startTime: startTimestamp,
            finishTime: finishTimestamp
        } = currentGroupValue;
        const defaultDate = moment().startOf('day');
        const startDate = startTimestamp ? moment(startTimestamp) : defaultDate;
        const endDate = finishTimestamp ? moment(finishTimestamp) : undefined;
        const startTime =
            startTimestamp && moment(startTimestamp).format('HH:mm');
        const endTime =
            finishTimestamp && moment(finishTimestamp).format('HH:mm');

        return {
            ...acc,
            [groupId]: {
                startDate,
                endDate,
                startTime,
                endTime
            }
        };
    }, {});
};

const tagCloudEditorFields = [AdditionalTargetingKey.SHOW_DATES];

export const mapInternalGroupValueToTagCloudEditor = (
    groupData: Record<string, EditInternalGroupData>
): TagCloudTargetingsState | undefined => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const groupIds = Object.keys(groupData);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId): TagCloudTargetingsState => {
        const groupValue = groupData[groupId].value;

        return tagCloudEditorFields.reduce(
            (resultState, field: AdditionalTargetingKey.SHOW_DATES) => {
                const fieldValue: TargetingShowDates | undefined =
                    groupValue[field];
                const key = getTagCloudTargetingId(
                    AdditionalTargetingKey.SHOW_DATES,
                    groupId
                );

                if (fieldValue === undefined) {
                    return {
                        ...resultState,
                        [key]: getDefaultTagCloudTargetingState()
                    };
                }

                const { targetingMode, value } = fieldValue;
                const splValue = value.map(it => it.trim());
                const targetingValue = splValue
                    .filter(isTargeting)
                    .map(it => [it]);
                const filteringValue = splValue
                    .filter(isFiltering)
                    .map(it => [it]);

                if (targetingMode === TARGETING) {
                    return {
                        ...resultState,
                        [`${field}_${groupId}`]: {
                            includedValue: targetingValue,
                            excludedValue: filteringValue,
                            isOpen: false,
                            canIncludeMinusValue: false,
                            savedValue: {
                                includedValue: targetingValue,
                                excludedValue: filteringValue
                            }
                        }
                    };
                }

                return {
                    ...resultState,
                    [`${field}_${groupId}`]: {
                        includedValue: filteringValue,
                        excludedValue: targetingValue,
                        isOpen: false,
                        canIncludeMinusValue: false,
                        savedValue: {
                            includedValue: filteringValue,
                            excludedValue: targetingValue
                        }
                    }
                };
            },
            acc
        );
    }, {});
};

export const mapRadioButtonGroupValueChangeToTargetingValueItem = (
    includedValue: string[],
    excludedValue: string[],
    newActiveRadio: TernaryControlOwnValue,
    toggleId: string
): TargetingValueItem => {
    const currentActiveRadio: TernaryControlOwnValue = getValueForToggle(
        toggleId,
        includedValue,
        excludedValue
    );

    let includedValueNew: string[];
    let excludedValueNew: string[];

    if (currentActiveRadio === TernaryControlOwnValue.YES) {
        includedValueNew = includedValue.filter(it => it !== toggleId);
    } else {
        includedValueNew =
            newActiveRadio === TernaryControlOwnValue.YES
                ? [...includedValue, toggleId]
                : includedValue;
    }

    if (currentActiveRadio === TernaryControlOwnValue.NO) {
        excludedValueNew = excludedValue.filter(it => it !== toggleId);
    } else {
        excludedValueNew =
            newActiveRadio === TernaryControlOwnValue.NO
                ? [...excludedValue, toggleId]
                : excludedValue;
    }

    return {
        includedValue: includedValueNew,
        excludedValue: excludedValueNew
    };
};
